import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { resolveRichText } from '@vibesites/runtime';
import {
  usePropertyField,
  useContentToken,
  useSlotGroup,
  useVibesites,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { PetsAllowedIcon } from '@vibesites/assets';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// Matches an empty <p> tag — empty meaning truly empty, whitespace-only,
// or containing only &nbsp; / <br> filler that some richtext editors emit.
const EMPTY_P = String.raw`<p\b[^>]*>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>`;
const LEADING_EMPTY_P_RE = new RegExp(`^(?:\\s*${EMPTY_P}\\s*)+`, 'i');
const TRAILING_EMPTY_P_RE = new RegExp(`(?:\\s*${EMPTY_P}\\s*)+$`, 'i');

function stripEdgeEmptyParagraphs(html: string): string {
  return html.replace(LEADING_EMPTY_P_RE, '').replace(TRAILING_EMPTY_P_RE, '').trim();
}

/** Homestead default breed list — also inlined on petpolicy.restrictions.defaultValue. */
const DEFAULT_PET_RESTRICTIONS_HTML =
  '<ul><li>Pit Bull &amp; Pit Bull Mixes</li><li>American Pit Bull Terrier</li><li>American Staffordshire Terrier</li><li>Wolf Hybrids</li><li>Doberman Pinscher</li><li>Saint Bernard</li><li>American Bulldog</li><li>Staffordshire Bull Terrier</li><li>Rottweiler</li><li>German Shepherd</li><li>Chow Chow</li><li>Great Dane</li><li>Akita</li></ul>';

/**
 * Multihub often stores the stock phrase "Breed restrictions apply" instead of
 * an actual list. Treat that (and empty/near-empty HTML) as a placeholder so
 * we can show the theme default breed list instead. Real custom copy is left alone.
 */
function isBreedRestrictionsPlaceholder(html: string | null | undefined): boolean {
  if (html == null) return true;
  const text = html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&#160;/g, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/[.!]+$/g, '')
    .trim();
  return text === '' || text === 'breed restrictions apply';
}

/**
 * Property pet restrictions are often stored as one breed per paragraph
 * (or one breed per newline / <br>). Convert those into a real bullet list
 * so they read as bullets instead of stacked paragraphs. Leave existing
 * <ul>/<ol> markup alone.
 *
 * A trailing disclaimer (longer prose after the breed names) is kept as a
 * normal paragraph below the list — not as a bullet.
 */
function toRestrictionsBulletList(html: string): string {
  const trimmed = html.trim();
  if (!trimmed) return '';
  if (/<(ul|ol)\b/i.test(trimmed)) return trimmed;

  const normalizeItem = (text: string) =>
    text.replace(/<br\s*\/?>/gi, ' ').replace(/\s+/g, ' ').trim();

  const isEmptyItem = (text: string) =>
    text.length === 0 || /^(?:&nbsp;|\s)*$/i.test(text);

  const stripTags = (text: string) =>
    text.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim();

  /** Trailing prose note after short breed names — not another restricted breed. */
  const isDisclaimer = (item: string, priorItems: string[]): boolean => {
    const plain = stripTags(item);
    if (!plain || priorItems.length === 0) return false;

    const wordCount = plain.split(/\s+/).filter(Boolean).length;
    const priorWordCounts = priorItems
      .map((p) => stripTags(p).split(/\s+/).filter(Boolean).length)
      .filter((n) => n > 0);
    const maxPriorWords = priorWordCounts.length ? Math.max(...priorWordCounts) : 0;
    const priorLengths = priorItems.map((p) => stripTags(p).length).filter((n) => n > 0);
    const avgPrior =
      priorLengths.length > 0
        ? priorLengths.reduce((sum, n) => sum + n, 0) / priorLengths.length
        : 0;

    // Breed names are short labels; disclaimers are full sentences / notes.
    if (wordCount >= Math.max(10, maxPriorWords + 4)) return true;
    if (/[.!?]\s*$/.test(plain) && wordCount >= 6) return true;
    if (
      /\b(please|note|disclaimer|management|reserves|subject to|additional|may be|not limited|all[- ]inclusive|contact|call for|apply|aggress|regardless)\b/i.test(
        plain,
      ) &&
      wordCount >= 5
    ) {
      return true;
    }
    if (plain.length >= Math.max(72, avgPrior * 2.5) && wordCount >= 6) return true;

    return false;
  };

  const splitDisclaimer = (items: string[]): { bullets: string[]; disclaimer: string | null } => {
    if (items.length < 2) return { bullets: items, disclaimer: null };
    const last = items[items.length - 1];
    const prior = items.slice(0, -1);
    if (isDisclaimer(last, prior)) {
      return { bullets: prior, disclaimer: last };
    }
    return { bullets: items, disclaimer: null };
  };

  const renderList = (items: string[], escapePlain = false): string => {
    const { bullets, disclaimer } = splitDisclaimer(items);
    if (bullets.length < 2 && !disclaimer) {
      return items.map((item) => `<p>${item}</p>`).join('');
    }
    const escape = (s: string) =>
      s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    const format = (s: string) => (escapePlain ? escape(s) : s);
    const listHtml =
      bullets.length > 0
        ? `<ul>${bullets.map((item) => `<li>${format(item)}</li>`).join('')}</ul>`
        : '';
    const disclaimerHtml = disclaimer ? `<p>${format(disclaimer)}</p>` : '';
    return `${listHtml}${disclaimerHtml}`;
  };

  const paragraphs = [...trimmed.matchAll(/<p\b[^>]*>([\s\S]*?)<\/p>/gi)].map((match) =>
    match[1].trim(),
  );

  // Multiple <p> tags → one bullet each (trailing disclaimer peeled off)
  if (paragraphs.length >= 2) {
    const items = paragraphs.map(normalizeItem).filter((text) => !isEmptyItem(text));
    if (items.length >= 2) {
      return renderList(items);
    }
  }

  // Single <p> with <br>-separated lines → one bullet each
  if (paragraphs.length === 1 && /<br\s*\/?>/i.test(paragraphs[0])) {
    const items = paragraphs[0]
      .split(/<br\s*\/?>/i)
      .map(normalizeItem)
      .filter((text) => !isEmptyItem(text));
    if (items.length >= 2) {
      return renderList(items);
    }
  }

  // Plain text with newlines (no HTML tags)
  if (!/<[a-z][\s\S]*>/i.test(trimmed)) {
    const lines = trimmed
      .split(/\r?\n+/)
      .map((line) => line.trim())
      .filter(Boolean);
    if (lines.length >= 2) {
      return renderList(lines, true);
    }
  }

  return trimmed;
}

const ctaLinkDef: SlotGroupDefinition = {
  label: 'View Amenities CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/amenities',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'View Amenities',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

function hasValue(val: unknown): val is string {
  if (val === null || val === undefined) return false;
  const s = String(val).trim().toLowerCase();
  return (
    s !== '' &&
    s !== 'false' &&
    s !== 'null' &&
    s !== 'undefined' &&
    s !== '0' &&
    s !== 'none' &&
    s !== 'n/a' &&
    s !== 'na'
  );
}

function isTruthy(val: unknown): boolean {
  if (val === true) return true;
  if (!val) return false;
  const s = String(val).trim().toLowerCase();
  return s === 'true' || s === 'yes' || s === '1';
}

function formatCurrency(val: string): string {
  const num = parseFloat(val.replace(/[^0-9.]/g, ''));
  if (isNaN(num)) return val;
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

// ============================================================================
// Component
// ============================================================================

export function PetPolicy() {
  const { property } = useVibesites();
  const allowsCats = usePropertyField('property_allows_cats');
  const allowsDogs = usePropertyField('property_allows_dogs');
  const policyDescription = usePropertyField('property_pet_policy_description') as string | null;
  const policyInfo = usePropertyField('property_petpolicy_info') as string | null;

  // Resolve restrictions richtext ourselves so we can strip leading/trailing
  // empty paragraphs (including <p>&nbsp;</p>, <p><br></p>, whitespace-only)
  // that the CSS `p:empty` rule can't catch. Property text wins when it is
  // real copy; Multihub's stock "Breed restrictions apply" (or empty) falls
  // back to the theme default breed list.
  const restrictionsRaw = useContentToken('petpolicy.restrictions');
  const restrictionsResolved = restrictionsRaw
    ? toRestrictionsBulletList(
        stripEdgeEmptyParagraphs(resolveRichText(restrictionsRaw, property as Record<string, any>)),
      )
    : '';
  const restrictionsHtml = isBreedRestrictionsPlaceholder(restrictionsResolved)
    ? DEFAULT_PET_RESTRICTIONS_HTML
    : restrictionsResolved;

  const ctaLink = useSlotGroup('petpolicy.cta_link', ctaLinkDef);

  const petDepositSublabel = useContentToken('petpolicy.pet_deposit_sublabel');
  const petFeeSublabel = useContentToken('petpolicy.pet_fee_sublabel');

  // Stat values — defaultField in the schema pulls the property value; authors can override per-site.
  const maxPetsValue = useContentToken('petpolicy.max_pets_value');
  const maxWeightValue = useContentToken('petpolicy.max_weight_value');
  const petDepositValue = useContentToken('petpolicy.pet_deposit_value');
  const petFeeValue = useContentToken('petpolicy.pet_fee_value');
  const monthlyRentValue = useContentToken('petpolicy.monthly_rent_value');

  const catsDogsList = [
    isTruthy(allowsDogs) && 'Dogs',
    isTruthy(allowsCats) && 'Cats',
  ].filter(Boolean);

  // Editable welcome text — authors can override it per-site.
  const welcomeText = useContentToken('petpolicy.welcome_text');

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  // Show the welcome badge if either pet type is allowed by the property
  // OR if an author has explicitly set a custom welcome text.
  const showWelcomeBadge = catsDogsList.length > 0 || Boolean(welcomeText);

  const showMaxPets = hasValue(maxPetsValue);
  const showMaxWeight = hasValue(maxWeightValue);
  const showPetDeposit = hasValue(petDepositValue);
  const showPetFee = hasValue(petFeeValue);
  const showMonthlyRent = hasValue(monthlyRentValue);
  const showStats =
    showMaxPets || showMaxWeight || showPetDeposit || showPetFee || showMonthlyRent;

  const descriptionHtml = policyDescription || policyInfo || null;

  return (
    <section ref={sectionRef} className="reveal-fade-up py-12 md:py-20 bg-[var(--vibe-color-background)]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="pet-layout">
          {/* Left column: description + restrictions */}
          <div className="pet-content">
            {/* Pets welcome badge */}
            {showWelcomeBadge && (
              <div className="flex items-center gap-3 mb-6">
                <PetsAllowedIcon size={28} color="var(--vibe-color-primary)" />
                <RichTextContent
                  path="petpolicy.welcome_text"
                  as="span"
                  className="font-heading text-lg text-black font-medium tracking-wide uppercase"
                />
              </div>
            )}

            {/* Policy description */}
            {descriptionHtml && (
              <RichTextContent
                path="petpolicy.description"
                label="Pet Policy Description"
                defaultField="property_pet_policy_description"
                as="div"
                className="font-body text-[0.95rem] leading-relaxed text-[var(--vibe-color-text)] mb-6 [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_li]:mb-1 [&_a]:text-primary [&_a]:underline"
              />
            )}

            {/* Pet restrictions — real Multihub copy when present; stock
                "Breed restrictions apply" / empty → default breed list */}
            {hasValue(restrictionsHtml) && (
              <div className="pet-restrictions">
                <RichTextContent
                  path="petpolicy.restrictions_heading"
                  as="h3"
                  className="font-heading text-base font-medium text-[var(--vibe-color-text)] mb-3 tracking-wide uppercase"
                />
                <EditableToken
                  path="petpolicy.restrictions"
                  type="richtext"
                  label="Pet Restrictions"
                  as="div"
                  className="font-body text-sm leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-2 [&_p:empty]:hidden [&_p:empty]:m-0 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_li]:mb-0.5 [&_a]:text-primary [&_a]:underline"
                >
                  <span
                    dangerouslySetInnerHTML={{ __html: restrictionsHtml }}
                    style={{ display: 'contents' }}
                  />
                </EditableToken>
              </div>
            )}

            {/* CTA */}
            <div className="mt-8">
              <EditableSlotGroup groupPath="petpolicy.cta_link" definition={ctaLinkDef}>
                <a
                  href={ctaLink.url || '/amenities'}
                  target={ctaLink._target || '_self'}
                  className="pet-cta"
                >
                  {ctaLink.label || 'View Amenities'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>

          {/* Right column: stat cards */}
          {showStats && (
            <div className="pet-stats">
              {showMaxPets && (
                <div className="pet-stat-card">
                  <RichTextContent path="petpolicy.max_pets_label" as="span" className="pet-stat-label" />
                  <EditableToken path="petpolicy.max_pets_value" as="span" className="pet-stat-value">
                    {maxPetsValue ? `${maxPetsValue} Allowed` : ''}
                  </EditableToken>
                </div>
              )}
              {showMaxWeight && (
                <div className="pet-stat-card">
                  <RichTextContent path="petpolicy.max_weight_label" as="span" className="pet-stat-label" />
                  <EditableToken path="petpolicy.max_weight_value" as="span" className="pet-stat-value">
                    {maxWeightValue ? `${maxWeightValue} lbs` : ''}
                  </EditableToken>
                </div>
              )}
              {showPetDeposit && (
                <div className="pet-stat-card">
                  <RichTextContent path="petpolicy.pet_deposit_label" as="span" className="pet-stat-label" />
                  <EditableToken path="petpolicy.pet_deposit_value" as="span" className="pet-stat-value">
                    {petDepositValue ? formatCurrency(petDepositValue) : ''}
                  </EditableToken>
                  <EditableToken path="petpolicy.pet_deposit_sublabel" as="span" className="pet-stat-sublabel">
                    {petDepositSublabel || '(Refundable)'}
                  </EditableToken>
                </div>
              )}
              {showPetFee && (
                <div className="pet-stat-card">
                  <RichTextContent path="petpolicy.pet_fee_label" as="span" className="pet-stat-label" />
                  <EditableToken path="petpolicy.pet_fee_value" as="span" className="pet-stat-value">
                    {petFeeValue ? formatCurrency(petFeeValue) : ''}
                  </EditableToken>
                  <EditableToken path="petpolicy.pet_fee_sublabel" as="span" className="pet-stat-sublabel">
                    {petFeeSublabel || '(Non-Refundable)'}
                  </EditableToken>
                </div>
              )}
              {showMonthlyRent && (
                <div className="pet-stat-card">
                  <RichTextContent path="petpolicy.monthly_rent_label" as="span" className="pet-stat-label" />
                  <EditableToken path="petpolicy.monthly_rent_value" as="span" className="pet-stat-value">
                    {monthlyRentValue ? `${formatCurrency(monthlyRentValue)}/mo` : ''}
                  </EditableToken>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`
        .pet-layout {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2.5rem;
        }
        @media (min-width: 768px) {
          .pet-layout {
            grid-template-columns: 1fr 1fr;
            gap: 3rem;
            align-items: start;
          }
        }

        .pet-content {
          order: 2;
        }
        @media (min-width: 768px) {
          .pet-content {
            order: 1;
          }
        }

        .pet-stats {
          order: 1;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.75rem;
        }
        @media (min-width: 768px) {
          .pet-stats {
            order: 2;
          }
        }

        .pet-stat-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 1.25rem 1rem;
          border: 1px solid #e8e8ea;
          background: #f7f7f8;
          border-radius: 4px;
          min-height: 100px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.06);
        }

        .pet-stat-label {
          font-family: var(--vibe-font-heading);
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--vibe-color-text);
          margin-bottom: 0.5rem;
        }

        .pet-stat-value {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text-muted);
        }

        .pet-stat-sublabel {
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          color: var(--vibe-color-text-muted);
          opacity: 0.7;
          margin-top: 0.15rem;
        }

        .pet-restrictions {
          padding: 1rem 1.25rem;
          background: #f7f7f8;
          border-left: 3px solid var(--vibe-color-primary);
          margin-bottom: 0.5rem;
        }

        .pet-cta {
          display: inline-block;
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: #000;
          background: transparent;
          border: 2px solid var(--vibe-color-primary);
          padding: 0.75rem 2rem;
          border-radius: 2px;
          text-decoration: none;
          /* Hover (secondary fill + lift) comes from Header-injected CTA_HOVER_CSS */
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PetPolicy',
  displayName: 'Pet Policy',
  tokens: {
    'petpolicy.welcome_text': {
      label: 'Pets Welcome Text',
      type: 'richtext',
      description: 'Badge text shown next to the paws icon.',
      defaultValue: '<p>Dogs &amp; Cats Welcome</p>',
    },
    'petpolicy.description': {
      label: 'Pet Policy Description',
      type: 'richtext',
      defaultField: 'property_pet_policy_description',
      suggestedFields: ['property_pet_policy_description', 'property_petpolicy_info'],
    },
    'petpolicy.restrictions_heading': {
      label: 'Pet Restrictions Heading',
      type: 'richtext',
      description: 'Heading shown above the pet restrictions list.',
      defaultValue: '<p>Pet Restrictions</p>',
    },
    'petpolicy.restrictions': {
      label: 'Pet Restrictions',
      type: 'richtext',
      defaultField: 'property_pet_restrictions',
      defaultValue:
        '<ul><li>Pit Bull &amp; Pit Bull Mixes</li><li>American Pit Bull Terrier</li><li>American Staffordshire Terrier</li><li>Wolf Hybrids</li><li>Doberman Pinscher</li><li>Saint Bernard</li><li>American Bulldog</li><li>Staffordshire Bull Terrier</li><li>Rottweiler</li><li>German Shepherd</li><li>Chow Chow</li><li>Great Dane</li><li>Akita</li></ul>',
      description:
        'Restricted breeds and other pet limits. Uses property_pet_restrictions when it has real copy; Multihub\'s "Breed restrictions apply" placeholder (or empty) shows the theme default breed list.',
    },
    'petpolicy.max_pets_label': {
      label: 'Pets Per Household Label',
      type: 'richtext',
      defaultValue: '<p>Pets Per Household</p>',
    },
    'petpolicy.max_pets_value': {
      label: 'Pets Per Household Value',
      type: 'text',
      defaultField: 'property_max_pets',
      description:
        'Number of pets allowed per household. Pulled from the property automatically; override here to display a custom value.',
    },
    'petpolicy.max_weight_label': {
      label: 'Max Weight Label',
      type: 'richtext',
      defaultValue: '<p>Max Weight</p>',
    },
    'petpolicy.max_weight_value': {
      label: 'Max Weight Value',
      type: 'text',
      defaultField: 'property_max_pet_weight_limit',
      description:
        'Maximum pet weight in lbs. Pulled from the property automatically; override here to display a custom value.',
    },
    'petpolicy.pet_deposit_label': {
      label: 'Pet Deposit Label',
      type: 'richtext',
      defaultValue: '<p>Pet Deposit</p>',
    },
    'petpolicy.pet_deposit_value': {
      label: 'Pet Deposit Value',
      type: 'text',
      defaultField: 'property_pet_deposit',
      description:
        'Pet deposit amount. Pulled from the property automatically; override here to display a custom value.',
    },
    'petpolicy.pet_deposit_sublabel': {
      label: 'Pet Deposit Sublabel',
      type: 'text',
      defaultValue: '(Refundable)',
    },
    'petpolicy.pet_fee_label': {
      label: 'Pet Fee Label',
      type: 'richtext',
      defaultValue: '<p>Pet Fee</p>',
    },
    'petpolicy.pet_fee_value': {
      label: 'Pet Fee Value',
      type: 'text',
      defaultField: 'property_upfront_pet_fee',
      description:
        'Upfront pet fee amount. Pulled from the property automatically; override here to display a custom value.',
    },
    'petpolicy.pet_fee_sublabel': {
      label: 'Pet Fee Sublabel',
      type: 'text',
      defaultValue: '(Non-Refundable)',
    },
    'petpolicy.monthly_rent_label': {
      label: 'Monthly Pet Rent Label',
      type: 'richtext',
      defaultValue: '<p>Monthly Pet Rent</p>',
    },
    'petpolicy.monthly_rent_value': {
      label: 'Monthly Pet Rent Value',
      type: 'text',
      defaultField: 'property_monthly_pet_rent',
      description:
        'Monthly pet rent amount. Pulled from the property automatically; override here to display a custom value.',
    },
  },
  slotGroups: {
    'petpolicy.cta_link': {
      label: 'View Amenities CTA',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultValue: '/amenities',
        },
        label: {
          label: 'Link Text',
          type: 'text',
          defaultValue: 'View Amenities',
        },
        _target: {
          label: 'Target',
          type: 'text',
          defaultValue: '_self',
        },
      },
    },
  },
};

(PetPolicy as any).contentSchema = contentSchema;

export default PetPolicy;
